import { Box, Typography } from '@mui/material'
import { styled, alpha } from '@mui/material/styles'

/**
 * Enhanced Info Field styled components
 * Modern, clean styling for profile card fields
 */

/**
 * Field container with subtle hover effect and better spacing
 */
export const EnhancedFieldContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.2s ease-in-out',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}))

/**
 * Field label - smaller, uppercase, with letter spacing for modern look
 */
export const EnhancedFieldLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 600,
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: theme.spacing(0.5),
}))

/**
 * Field value - prominent, clean typography
 */
export const EnhancedFieldValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  fontSize: '0.95rem',
  lineHeight: 1.4,
}))

/**
 * Empty field value placeholder
 */
export const EnhancedFieldEmpty = styled(Typography)(({ theme }) => ({
  color: alpha(theme.palette.text.secondary, 0.5),
  fontWeight: 400,
  fontSize: '0.9rem',
  fontStyle: 'italic',
}))

/**
 * Link value - styled for clickable fields
 */
export const EnhancedFieldLink = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
  fontSize: '0.95rem',
  lineHeight: 1.4,
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: theme.palette.primary.dark,
    textDecoration: 'underline',
  },
}))

/**
 * Section header - for grouping related fields
 */
export const EnhancedSectionHeader = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 700,
  fontSize: '1rem',
  marginBottom: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}))

/**
 * Section container with subtle background
 */
export const EnhancedSectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.5),
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}))

/**
 * Icon wrapper for field icons
 */
export const FieldIconWrapper = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: theme.spacing(0.75),
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  marginRight: theme.spacing(1),
  '& svg': {
    fontSize: '1rem',
    color: theme.palette.primary.main,
  },
}))

/**
 * Field row with icon
 */
export const EnhancedFieldRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
}))

/**
 * Tag/chip style for multi-value fields
 */
export const FieldTag = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.spacing(2),
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  fontSize: '0.8rem',
  fontWeight: 500,
  marginRight: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
}))

/**
 * Avatar/image container for user fields
 */
export const FieldAvatarContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
}))
